<template>
  <div
    ref="waterfallWrapper"
    class="WaterfallList"
    :style="{ height: list.length === 0 ? 'auto' : `${wrapperHeight}px` }"
  >
    <div v-if="!loading && list.length === 0">
      <slot name="empty"> </slot>
    </div>
    <template v-else>
      <div
        v-for="(item, index) in list"
        role="waterfall-item"
        :key="`waterfall-${index}`"
        class="WaterfallItem"
      >
        <div class="waterfall-card">
          <slot v-if="loading" name="loadingItem" :item="item" :index="index">
            <div class="LoadingBlock"></div
          ></slot>
          <slot v-else name="item" :item="item" :index="index"></slot>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
  import { defineComponent, onMounted, provide, ref, watch } from 'vue';
  import { useThrottleFn } from '@vueuse/core';
  import { useCalculateCols, useLayout } from '../use';

  export default defineComponent({
    props: {
      list: {
        type: Array,
        default: () => [],
      },
      rowKey: {
        type: String,
        default: 'id',
      },
      width: {
        type: Number,
        default: 200,
      },
      breakpoints: {
        type: Object,
        default: () => ({
          3000: {
            rowPerView: 6,
          },
          1700: {
            rowPerView: 5,
          },
          1200: {
            // when wrapper width < 1200
            rowPerView: 4,
          },
          800: {
            // when wrapper width < 800
            rowPerView: 3,
          },
          500: {
            // when wrapper width < 500
            rowPerView: 2,
          },
        }),
      },
      gutter: {
        type: Number,
        default: 10,
      },
      hasAroundGutter: {
        type: Boolean,
        default: true,
      },
      animationPrefix: {
        type: String,
        default: 'animate__animated',
      },
      animationEffect: {
        type: String,
        default: 'fadeIn',
      },
      animationDuration: {
        type: Number,
        default: 1000,
      },
      animationDelay: {
        type: Number,
        default: 300,
      },
      lazyload: {
        type: Boolean,
        default: true,
      },
      loadProps: {
        type: Object,
        default: () => {},
      },
      crossOrigin: {
        type: Boolean,
        default: true,
      },
      delay: {
        type: Number,
        default: 300,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      skeletonBlockLength: {
        type: Number,
        default: 20,
      },
    },
    setup(props) {
      // 容器块信息
      const { waterfallWrapper, wrapperWidth, colWidth, cols, offsetX } = useCalculateCols(props);

      // 容器高度，块定位
      const { wrapperHeight, layoutHandle } = useLayout(
        props,
        colWidth,
        cols,
        offsetX,
        waterfallWrapper,
      );

      // 减少性能开销
      const renderer = useThrottleFn(() => {
        layoutHandle();
      }, props.delay);

      // 列表发生变化直接触发排版
      watch(
        () => [wrapperWidth, colWidth],
        () => {
          renderer();
        },
        { deep: true },
      );

      watch(
        () => props.list,
        () => {
          renderer();
        },
        {
          deep: true,
        },
      );
      watch(
        () => props.loading,
        () => {
          layoutHandle();
        },
      );

      // 尺寸宽度变化防抖触发
      const sizeChangeTime = ref(0);

      provide('sizeChangeTime', sizeChangeTime);

      // 图片加载完成
      provide('imgLoaded', renderer);

      onMounted(() => {
        const observer = new MutationObserver((mutationsList) => {
          // 遍历每个发生变化的元素
          for (let mutation of mutationsList) {
            const addedNodes = mutation.addedNodes;
            for (let j = 0; j < addedNodes.length; j++) {
              const addedNode = addedNodes[j];
              if (addedNode?.tagName === 'IMG') {
                setTimeout(() => {
                  renderer();
                }, 500);
              }
            }
            // 如果是子元素的属性发生了变化
            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
              setTimeout(() => {
                renderer();
              }, 500);
            }
          }
        });
        observer.observe(waterfallWrapper.value!, {
          childList: true,
          subtree: true,
          attributes: true,
        });
      });

      return {
        waterfallWrapper,
        wrapperHeight,
      };
    },
  });
</script>

<style scoped>
  /* 初始的入场效果 */
  @-webkit-keyframes fadeIn {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  .WaterfallList {
    width: 100%;
    position: relative;
    overflow: hidden;
  }

  .WaterfallItem {
    position: absolute;
    left: 0;
    top: 0;
    /* 初始位置设置到屏幕以外，避免懒加载失败 */
    transform: translate3d(0, 3000px, 0);
    visibility: hidden;
  }

  .LoadingBlock {
    height: 300px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #f6f8f9;
    border: 6px;
  }

  .fadeIn {
    -webkit-animation-name: fadeIn;
    animation-name: fadeIn;
  }
</style>
